/**
 * Third-person camera "spring arm" math: keeps the desktop/mobile orbit camera
 * inside the space the avatar is in. Pure functions on plain {x,y,z} / box
 * shapes (THREE.Vector3 and THREE.Box3 fit), no allocation, so the avatar's
 * frame loop can call them for every collider every frame.
 *
 * How the avatar uses it (FixedRetargetedAvatar):
 *  1. cast from the head toward the wanted camera spot; every blocker the ray
 *     meets shortens the arm to ARM_MARGIN in front of the hit;
 *  2. blockers are the collidable meshes/proxies the avatar already caches,
 *     minus anything topping out below the head (tables, seats), plus wide
 *     thin slabs overhead (a ceiling or upper floor that isn't flagged
 *     collidable — the Game Room's ceiling is one);
 *  3. walkable bounds (settings.bounds), when the world has them, act as
 *     invisible walls for the arm too, BOUNDS_INSET inside the rectangle —
 *     that catches gaps the colliders leave (a doorway, a seam between wall
 *     pieces);
 *  4. a final clamp keeps the camera above the floor and under an inferred
 *     ceiling.
 *
 * Tests: node --import tsx --test tests/cameraArm.test.ts
 */
import type { WorldBounds } from './worldBounds';

export interface Vec3Like { x: number; y: number; z: number }
export interface BoxLike { min: Vec3Like; max: Vec3Like }

/** Gap kept between the camera and whatever stopped the arm (m). */
export const ARM_MARGIN = 0.3;
/** Shortest the arm gets, so the camera never ends up inside the head. */
export const ARM_MIN = 0.6;
/** Camera stays this far inside the walkable-bounds rectangle (m). */
export const BOUNDS_INSET = 0.4;
/** Camera stays at least this high above the avatar's feet (m). */
export const FLOOR_CLEARANCE = 0.3;

/**
 * Distance along a unit-length ray to an axis-aligned box: 0 when the origin is
 * already inside, Infinity when the ray misses (or the box is behind it).
 */
export function rayBoxDistance(o: Vec3Like, d: Vec3Like, b: BoxLike): number {
  let tmin = 0;
  let tmax = Infinity;
  // Unrolled slab test per axis.
  if (Math.abs(d.x) < 1e-12) {
    if (o.x < b.min.x || o.x > b.max.x) return Infinity;
  } else {
    const inv = 1 / d.x;
    let t1 = (b.min.x - o.x) * inv, t2 = (b.max.x - o.x) * inv;
    if (t1 > t2) { const t = t1; t1 = t2; t2 = t; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return Infinity;
  }
  if (Math.abs(d.y) < 1e-12) {
    if (o.y < b.min.y || o.y > b.max.y) return Infinity;
  } else {
    const inv = 1 / d.y;
    let t1 = (b.min.y - o.y) * inv, t2 = (b.max.y - o.y) * inv;
    if (t1 > t2) { const t = t1; t1 = t2; t2 = t; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return Infinity;
  }
  if (Math.abs(d.z) < 1e-12) {
    if (o.z < b.min.z || o.z > b.max.z) return Infinity;
  } else {
    const inv = 1 / d.z;
    let t1 = (b.min.z - o.z) * inv, t2 = (b.max.z - o.z) * inv;
    if (t1 > t2) { const t = t1; t1 = t2; t2 = t; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return Infinity;
  }
  return tmin;
}

/**
 * A wide, thin, roughly horizontal box: a ceiling, roof or floor slab. Only
 * these non-collidable meshes are worth caching for the camera (they are what
 * a room's lid is made of); everything else non-collidable is decoration.
 */
export function isSlab(b: BoxLike): boolean {
  const dx = b.max.x - b.min.x, dy = b.max.y - b.min.y, dz = b.max.z - b.min.z;
  return dy <= 0.6 && dx >= 1.5 && dz >= 1.5;
}

/**
 * Does a collider stop the camera? Anything topping out below the head is
 * furniture (tables, seats, cabinets' bases): the camera always sits above the
 * head in third person, so these could only ever make it jitter.
 */
export function blocksCamera(b: BoxLike, headY: number): boolean {
  return b.max.y >= headY;
}

/** Is a slab overhead (a ceiling) rather than underfoot (a floor, a rug)? */
export function isOverhead(b: BoxLike, headY: number): boolean {
  return b.min.y > headY;
}

/** Does the box's footprint cover (x, z)? */
export function coversXZ(b: BoxLike, x: number, z: number): boolean {
  return x >= b.min.x && x <= b.max.x && z >= b.min.z && z <= b.max.z;
}

/** Is p within `clear` of the box on every axis (i.e. near or inside it)? */
export function nearBox(p: Vec3Like, b: BoxLike, clear: number): boolean {
  return p.x > b.min.x - clear && p.x < b.max.x + clear &&
    p.y > b.min.y - clear && p.y < b.max.y + clear &&
    p.z > b.min.z - clear && p.z < b.max.z + clear;
}

/**
 * Arm length after a hit `hitDist` along the ray: stop ARM_MARGIN short of it.
 * Returns `allowed` unchanged when the hit is beyond the arm (+ margin).
 */
export function shortenArm(allowed: number, hitDist: number, margin = ARM_MARGIN): number {
  return hitDist < allowed + margin ? Math.min(allowed, hitDist - margin) : allowed;
}

/** Bounds never hold the camera closer than this to the head on an axis (m). */
export const BOUNDS_MIN_GAP = 1.0;

/**
 * How far the arm may run (unit ray, x/z only) before leaving the bounds
 * rectangle shrunk by `inset`. Only the sides the ray moves toward count, so a
 * head standing in the inset strip (a player against the wall) still lets the
 * camera swing out into the room. Each side is also kept at least `minGap`
 * beyond the head: bounds are a backstop for gaps in the walls, and must not
 * pin the camera onto the head of someone standing at the edge (the walls'
 * own colliders still stop it there). Infinity with no bounds.
 */
export function rayExitBounds(o: Vec3Like, d: Vec3Like, b: WorldBounds | null, inset = BOUNDS_INSET, minGap = BOUNDS_MIN_GAP): number {
  if (!b) return Infinity;
  let t = Infinity;
  if (d.x > 1e-12) t = Math.min(t, (Math.max(b.max[0] - inset, o.x + minGap) - o.x) / d.x);
  else if (d.x < -1e-12) t = Math.min(t, (Math.min(b.min[0] + inset, o.x - minGap) - o.x) / d.x);
  if (d.z > 1e-12) t = Math.min(t, (Math.max(b.max[1] - inset, o.z + minGap) - o.z) / d.z);
  else if (d.z < -1e-12) t = Math.min(t, (Math.min(b.min[1] + inset, o.z - minGap) - o.z) / d.z);
  return t;
}

export interface CameraClamp {
  /** Lowest the camera may go (world y). */
  floorY: number;
  /** Highest the camera may go (world y); Infinity = no ceiling found. */
  ceilingY: number;
}

/**
 * Last line of defence, applied to the arm's result: above the floor, below
 * the ceiling. (Bounds go through the arm instead — rayExitBounds — so they
 * can never put the camera on top of the head.) Mutates `p` in place; returns
 * whether anything moved.
 */
export function clampCamera(p: Vec3Like, c: CameraClamp): boolean {
  let changed = false;
  // Ceiling first, floor wins if they cross (a crawlspace): never underground.
  if (p.y > c.ceilingY) { p.y = c.ceilingY; changed = true; }
  if (p.y < c.floorY) { p.y = c.floorY; changed = true; }
  return changed;
}
